import type { RefObject } from 'react';
import { ExternalLink } from 'lucide-react';
import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { appPromotionLogos, type AppPromotionSettings } from '@/config/appPromotion';
import { getAppStoreUrl } from '@/lib/appStoreUrls';

export function AppDownloadDialog({ settings, id, returnFocusRef }: {
  settings: AppPromotionSettings;
  id: string;
  returnFocusRef: RefObject<HTMLButtonElement>;
}) {
  return <DialogContent
    id={id}
    className="app-download-dialog w-[calc(100%-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl border-[#e6c798] bg-[#fff8ee] p-5 text-[#21180f] sm:p-6"
    onCloseAutoFocus={event => {
      event.preventDefault();
      returnFocusRef.current?.focus();
    }}
  >
    <DialogHeader className="pr-7 text-left">
      <DialogTitle className="text-2xl">Choose your app</DialogTitle>
      <DialogDescription className="leading-relaxed text-stone-600">Find DoPuja on your favourite app store. Available links open in a new tab.</DialogDescription>
    </DialogHeader>
    <div className="space-y-4">
      {(['pandit', 'doPuja'] as const).map(app => {
        const logo = appPromotionLogos[app];
        return <section key={app} aria-label={logo.name} className="rounded-lg border border-[#e6c798] bg-white p-4">
          <div className="mb-4 flex items-center gap-3">
            <img src={logo.src} alt={`${logo.name} app logo`} width="48" height="48" className="h-12 w-12 rounded-lg object-contain" />
            <h3 className="text-base font-semibold">{logo.name}</h3>
          </div>
          <div className="grid gap-2 min-[380px]:grid-cols-2">
            {(['GooglePlay', 'AppStore'] as const).map(store => {
              const key = `${app}${store}` as const;
              const url = getAppStoreUrl(settings.storeUrls?.[key], key);
              const label = store === 'GooglePlay' ? 'Google Play' : 'App Store';
              return url ? <a
                key={store}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${logo.name} on ${label} (opens in a new tab)`}
                className="flex min-h-12 items-center justify-between gap-2 rounded-md border border-[#965a0b] bg-[#965a0b] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#754507] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#754507]"
              >{label}<ExternalLink aria-hidden="true" className="h-4 w-4 shrink-0" /></a> : <div key={store} className="flex min-h-12 flex-col justify-center rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm">
                <span className="font-semibold text-stone-700">{label}</span>
                <span className="text-xs text-stone-600">Coming soon</span>
              </div>;
            })}
          </div>
        </section>;
      })}
    </div>
  </DialogContent>;
}
